(() => {
  'use strict';
  if (customElements.get('product-finder')) return;
  const escape = value => String(value ?? '').replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character]));
  const core = globalThis.StoreSuppliesFinder;
  const recommend = core.recommendation;

  class ProductFinder extends HTMLElement {
    static get observedAttributes() { return ['data-revlift-page-integration']; }

    attributeChangedCallback(name, previous, value) {
      if (previous === value || !this.initialized) return;
      this.subscriptionCatalog = null;
      this.offerLoadPromise = null;
      this.offerPlanId = null;
      if (value) this.prefetchOffer();
    }

    prefetchOffer() {
      const planId = this.getAttribute('data-revlift-page-integration');
      const sdk = globalThis.RevliftSubscriptions;
      if (!planId || !sdk?.loadOffer) return null;
      if (this.offerPlanId === planId && this.offerLoadPromise) return this.offerLoadPromise;
      this.offerPlanId = planId;
      this.offerError = null;
      this.offerLoadPromise = sdk.loadOffer(planId).then(catalog => {
        if (this.getAttribute('data-revlift-page-integration') !== planId) return;
        this.subscriptionCatalog = catalog;
        if (this.step === 'results' && !this.busy) this.renderResults(false);
      }).catch(() => {
        if (this.getAttribute('data-revlift-page-integration') !== planId) return;
        this.offerError = 'We could not load current subscription pricing. Please reload before ordering.';
        if (this.step === 'results') this.renderResults(false);
        else this.offerLoadPromise = null;
      });
      return this.offerLoadPromise;
    }

    prefetchPagePromo(fresh = false) {
      if (!fresh && this.pagePromoPromise) return this.pagePromoPromise;
      const url = new URL(`${this.config.rootUrl}apps/everything-app`, location.origin);
      url.searchParams.set('shop', globalThis.Shopify?.shop || location.hostname);
      if (globalThis.REVLIFT_COUNTRY_CODE) url.searchParams.set('country', globalThis.REVLIFT_COUNTRY_CODE);
      if (fresh) url.searchParams.set('_finder_promo', Date.now());
      this.pagePromoPromise = fetch(url, { cache: 'no-store', signal: AbortSignal.timeout(10000) }).then(response => {
        if (!response.ok) throw new Error('Page offer could not be checked.');
        return response.json();
      }).then(payload => {
        this.pagePromos = payload.config?.pageOffers || [];
        if (this.step === 'results' && !this.busy) this.renderResults(false);
      }).catch(() => { this.pagePromos = []; });
      return this.pagePromoPromise;
    }

    priceOneTime(result) {
      if (result.error) return result;
      // Support the quiz's simple quantity/percentage page promo. Unknown rules
      // remain undiscounted in the preview; Shopify is authoritative in cart.
      const offers = (this.pagePromos || []).filter(offer => {
        const c = offer.conditions || {};
        return offer.enabled !== false && offer.purchaseOptionMode !== 'subscription'
          && offer.pagePaths?.includes(location.pathname)
          && (!globalThis.REVLIFT_IS_B2B || offer.includeB2B === true)
          && !offer.marketIds?.length && !offer.audience
          && c.mode === 'any_total' && Number(c.minTotalQuantity || 1) === 1
          && !c.minSubtotalCents && !(c.items || []).length
          && (offer.tiers || []).every(tier => Number(tier.minQuantity || 1) === 1);
      });
      const lines = result.lines.map(line => {
        const percent = Math.max(0, ...offers.filter(offer => offer.storeWide || offer.conditions.collectionItems?.some(item =>
          line.product.collectionIds?.map(String).includes(String(item.collectionId))))
          .flatMap(offer => offer.tiers.map(tier => Number(tier.rewards?.discountPercent ?? tier.percentOff ?? 0)))
          .filter(value => value > 0 && value <= 100));
        return { ...line, unitPrice: line.variant.price - Math.floor(line.variant.price * percent / 100 + 1e-7) };
      });
      return { ...result, lines, total: lines.reduce((sum, line) => sum + line.unitPrice * line.quantity, 0) };
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.config = JSON.parse(this.querySelector('[data-finder-config]').textContent);
      this.screen = this.querySelector('[data-screen]');
      this.welcome = this.querySelector('[data-welcome]');
      this.answers = { productsPerMonth: 1000, months: 3, inkPacks: 1 };
      this.step = 'welcome';
      this.mode = '';
      this.offerChoice = null;
      this.busy = false;
      this.money = cents => new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency: this.config.currency }).format(cents / 100);
      this.addEventListener('click', event => this.click(event));
      this.addEventListener('input', event => this.input(event));
      this.addEventListener('change', event => this.change(event));
      this.cartStockHandler = event => { if (event.detail?.cart?.items) this.updateCartStock(event.detail.cart); };
      document.addEventListener('on:line-item:change', this.cartStockHandler);
      this.render();
      this.prefetchOffer();
      this.prefetchPagePromo();
      this.availabilityPromise = this.refreshAvailability().then(() => {
        this.stockReady = true;
        if (this.step === 'results' || this.step === 'style') this.render(false);
      }).catch(() => {
        this.stockReady = false;
        this.stockError = 'We could not check current availability. Please reload the quiz before ordering.';
        if (this.step === 'results') this.render(false);
      });
    }

    disconnectedCallback() {
      document.removeEventListener('on:line-item:change', this.cartStockHandler);
    }

    async refreshAvailability(fresh = false) {
      const cartRequest = fetch(`${this.config.rootUrl}cart.js`, { cache: 'no-store' }).then(response => {
        if (!response.ok) throw new Error('Cart availability check failed.');
        return response.json();
      });
      const [cart, models] = await Promise.all([cartRequest, fresh ? this.fetchCurrentModels() : Promise.resolve(this.config.models)]);
      return this.updateCartStock(cart, models);
    }

    async fetchCurrentModels() {
      const url = new URL(location.href);
      url.searchParams.set('section_id', this.dataset.sectionId);
      url.searchParams.set('_finder_stock', Date.now());
      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) throw new Error('Product availability check failed.');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const config = html.querySelector('[data-finder-config]');
      if (!config) throw new Error('Product availability check failed.');
      return JSON.parse(config.textContent).models;
    }

    async refreshSavedOffer() {
      const planId = this.getAttribute('data-revlift-page-integration');
      if (planId) this.subscriptionCatalog = await globalThis.RevliftSubscriptions.loadOffer(planId);
      this.renderResults(false);
    }

    updateCartStock(cart, models = this.config.models) {
      const quantities = new Map();
      for (const item of cart.items) quantities.set(item.variant_id, (quantities.get(item.variant_id) || 0) + item.quantity);
      this.config.models = models.map(model => {
        const next = { ...model };
        for (const kind of ['priceGun', 'dateGun', 'labels', 'ink']) if (model[kind]) next[kind] = { ...model[kind],
          variants: model[kind].variants.map(variant => ({ ...variant, cartQuantity: quantities.get(variant.id) || 0 })) };
        return next;
      });
      return cart;
    }

    art(name, className = '', alt = '', eager = false) {
      const art = this.config.art[name];
      if (['yes', 'noCircle', 'noCross'].includes(name)) className += ' finder__line-art';
      return art ? `<img class="${className}" src="${escape(art.url)}" alt="${escape(alt)}" width="${art.width}" height="${art.height}" loading="${eager ? 'eager' : 'lazy'}" ${eager ? 'fetchpriority="high"' : ''}>` : '';
    }

    primary(text, action = 'next') {
      return `<button class="finder__primary" type="button" data-action="${action}">${escape(text)} ${this.art('arrow', 'finder__arrow')}</button>`;
    }

    option(value, label, content) {
      const answer = this.answers[this.step];
      const selected = Array.isArray(answer) ? answer.includes(value) : answer === value;
      return `<button type="button" class="finder__option${selected ? ' finder__option--featured' : ''}" aria-pressed="${selected}" data-answer="${escape(value)}">
        <span class="finder__option-media">${content}</span><span>${escape(label)}</span></button>`;
    }

    model() { return core.chooseModel(this.config.models, this.answers); }

    hasAnswer() {
      const answer = this.answers[this.step];
      return Array.isArray(answer) ? answer.length > 0 : Boolean(answer);
    }

    colorPhoto(model, style) {
      const variant = core.labelVariants(model?.labels, style).filter(variant => variant.image)
        .sort((a, b) => core.packUnits(a) - core.packUnits(b))[0];
      return this.productPhoto(variant ? { image: variant.image, title: `${style} labels for ${model.name}` } : model?.labels);
    }

    productPhoto(product, alt = '') {
      if (!product?.image) return '';
      const source = product.image.replace(/([?&])width=\d+/, '$1width=400');
      return `<img class="finder__product-photo" src="${escape(source)}" alt="${escape(alt || product.title)}" width="400" height="400" loading="lazy">`;
    }

    gunProduct(model, print = this.answers.print) {
      return (print === 'date' ? model?.dateGun : model?.priceGun) || model?.priceGun || model?.dateGun;
    }

    suppliesPhotos(model) {
      return `<span class="finder__product-pair">${this.productPhoto(model?.labels)}${this.productPhoto(model?.ink)}</span>`;
    }

    labelExample(print, lines) {
      const key = `${print === 'date' ? 'date' : 'price'}${lines}Example`;
      return this.art(key, 'finder__label-example', this.config.art[key]?.alt || `${lines}-line ${print} label example`);
    }

    modelArt(model, alt = '', identify = false) {
      if (!identify && model?.brand === 'Perco' && this.config.art[model.artKey]) {
        return this.art(model.artKey, 'finder__product-photo', alt || model.name);
      }
      if (!identify) return this.productPhoto(this.gunProduct(model), alt);
      const key = { 'perco-pro-1': 'proOneKnob', 'perco-pro-2': 'proTwoKnobs' }[model?.key] || model?.artKey || 'lite';
      return this.art(key, '', alt);
    }

    click(event) {
      const choice = event.target.closest('[data-answer]');
      if (choice) {
        if (this.busy) return;
        this.answer(choice.dataset.answer);
        return;
      }
      const button = event.target.closest('[data-action]');
      if (!button) return;
      const action = button.dataset.action;
      if (this.busy) return;
      if (action === 'start') this.step = 'owner';
      else if (action === 'restart') { this.answers = { productsPerMonth: 1000, months: 3, inkPacks: 1 }; this.step = 'owner'; this.mode = ''; this.offerChoice = null; }
      else if (action === 'back') {
        if (this.step === 'contact') this.step = 'model';
        else {
          const steps = core.stepsFor(this.answers);
          this.step = steps[steps.indexOf(this.step) - 1] || 'welcome';
        }
      } else if (action === 'contact') this.step = 'contact';
      else if (action === 'next') {
        if (this.step === 'volume' && !this.querySelector('[data-volume]').reportValidity()) return;
        if (this.querySelector('[data-answer]') && !this.hasAnswer()) return;
        this.next(); return;
      } else if (action === 'one-time') {
        this.offerChoice = 'once';
        this.lastSupplyOffer = null;
        this.render(false);
        this.addSelectionToCart(); return;
      } else if (action === 'confirm-mix') {
        this.answers.colorMixConfirmed = true;
        this.offerChoice = null;
        this.render(false); return;
      } else if (action === 'add-to-cart') { this.addSelectionToCart(); return; }
      this.render();
    }

    answer(value) {
      const field = this.step;
      if (field === 'style') {
        if (!core.styleOptions(this.model()?.labels).includes(value)) return;
        const selected = core.selectedStyles(this.answers.style || []);
        this.answers.style = selected.includes(value) ? selected.filter(style => style !== value) : [...selected, value];
        delete this.answers.colorMix;
        delete this.answers.colorMixConfirmed;
        delete this.answers.excludedColors;
        delete this.answers.inkOverrides;
        delete this.answers.excludedInkVariants;
        this.mode = '';
        this.offerChoice = null;
        this.render(false);
        return;
      }
      const resets = {
        owner: ['brand', 'model', 'print', 'lines', 'extras', 'supplies', 'style'],
        brand: ['model', 'supplies', 'style'], model: ['supplies', 'style'],
        print: ['lines', 'extras', 'supplies', 'style'], lines: ['extras', 'supplies', 'style'],
        extras: ['supplies', 'style'], supplies: ['style']
      };
      if (this.answers[field] !== value) for (const key of resets[field] || []) delete this.answers[key];
      if (this.answers[field] !== value) {
        for (const key of ['colorMix', 'colorMixConfirmed', 'excludedColors', 'inkOverrides', 'inkRestore', 'excludedInkVariants', 'includeInk']) delete this.answers[key];
      }
      this.answers[field] = value;
      this.mode = '';
      this.offerChoice = null;
      this.render(false);
    }

    next() {
      const steps = core.stepsFor(this.answers);
      this.step = steps[steps.indexOf(this.step) + 1] || 'results';
      this.render();
    }

    input(event) {
      if (this.busy) return;
      if (event.target.matches('[data-volume], [data-volume-slider]')) {
        delete this.answers.inkOverrides;
        delete this.answers.inkRestore;
        const value = Number(event.target.value);
        this.answers.productsPerMonth = value;
        const number = this.querySelector('[data-volume]');
        const slider = this.querySelector('[data-volume-slider]');
        if (event.target !== number) number.value = value;
        if (event.target !== slider) slider.value = value;
      }
    }

    change(event) {
      if (this.busy) return;
      if (event.target.matches('[data-color-percent]')) {
        const styles = core.selectedStyles(this.answers.style);
        this.answers.colorMix = core.adjustColorMix(styles, this.answers.colorMix, event.target.dataset.colorPercent, event.target.value);
        this.answers.colorMixConfirmed = false;
        this.render(false); return;
      }
      if (event.target.matches('[data-include-color]')) {
        if (this.lastSupplyOffer) this.offerChoice = this.lastSupplyOffer;
        const excluded = new Set(this.answers.excludedColors || []);
        if (event.target.checked) excluded.delete(event.target.dataset.includeColor);
        else excluded.add(event.target.dataset.includeColor);
        this.answers.excludedColors = [...excluded];
        this.render(false); return;
      }
      if (event.target.matches('[data-include-ink]')) {
        if (this.lastSupplyOffer) this.offerChoice = this.lastSupplyOffer;
        const id = event.target.dataset.includeInk;
        if (id) {
          const months = this.answers.months;
          const excluded = new Set((this.answers.excludedInkVariants || []).map(String));
          const restore = { ...this.answers.inkRestore?.[months] };
          if (event.target.checked) {
            excluded.delete(id);
            if (this.answers.inkOverrides?.[months]?.[id] === 0) this.answers.inkOverrides[months][id] = restore[id] || 1;
          } else {
            restore[id] = this.result.inkChoices.find(line => String(line.variant.id) === id)?.requestedQuantity || 1;
            excluded.add(id);
          }
          this.answers.inkRestore = { ...this.answers.inkRestore, [months]: restore };
          this.answers.excludedInkVariants = [...excluded];
          this.answers.includeInk = true;
        } else {
          this.answers.includeInk = event.target.checked;
          if (event.target.checked) { delete this.answers.inkOverrides; delete this.answers.excludedInkVariants; }
        }
        this.render(false); return;
      }
      if (event.target.matches('[data-ink-quantity]')) {
        const months = this.answers.months;
        const quantities = Object.fromEntries(this.result.inkChoices.map(line => [line.variant.id, line.requestedQuantity ?? line.quantity]));
        const line = this.result.inkChoices.find(line => String(line.variant.id) === event.target.dataset.inkQuantity);
        const maximum = Math.min(9999, core.inventoryLimit(line.variant));
        quantities[line.variant.id] = Math.max(0, Math.min(maximum, Math.round(Number(event.target.value) || 0)));
        const excluded = new Set((this.answers.excludedInkVariants || []).map(String));
        if (quantities[line.variant.id] > 0) excluded.delete(String(line.variant.id));
        else {
          excluded.add(String(line.variant.id));
          this.answers.inkRestore = { ...this.answers.inkRestore, [months]: { ...this.answers.inkRestore?.[months], [line.variant.id]: line.requestedQuantity || 1 } };
        }
        this.answers.excludedInkVariants = [...excluded];
        this.answers.inkOverrides = { ...this.answers.inkOverrides, [months]: quantities };
        this.render(false); return;
      }
      if (event.target.matches('[data-offer]')) {
        const value = event.target.value;
        this.offerChoice = value;
        if (value !== 'once') this.lastSupplyOffer = value;
        if (value !== 'once') this.answers.months = Number(value);
        this.render(false);
        this.querySelector(`[data-offer][value="${value}"]`)?.focus({ preventScroll: true });
      }
    }

    render(focus = true) {
      this.welcome.hidden = true;
      let body = '';
      let title = '';
      const a = this.answers;
      const model = this.model();
      const exampleModel = this.config.models.find(item => item.key === 'perco-lite');
      if (this.step === 'welcome') {
        this.screen.innerHTML = `<div class="finder__hero">
          <div class="finder__hero-media"><picture>${this.config.art.heroMobile ? `<source media="(max-width: 749px)" srcset="${escape(this.config.art.heroMobile.url)}" width="${this.config.art.heroMobile.width}" height="${this.config.art.heroMobile.height}">` : ''}${this.art('hero', '', 'Perco gun, label rolls and ink', true)}</picture></div>
          <div class="finder__hero-copy"><h1 tabindex="-1">${escape(this.config.heading)}</h1><p class="finder__hero-description">${escape(this.config.introduction)}</p>
          ${this.config.discountCode && this.config.discountCopy ? `<p class="finder__offer">${this.art('offer')}${escape(this.config.discountCopy)}</p>` : ''}
          <div class="finder__hero-actions"><p class="finder__meta">${this.art('clock')}About 2 min</p>${this.primary('Start Now', 'start')}</div>
          <p class="finder__trust"><span>${this.art('star').repeat(5)}</span><span>100,000+ stores &middot; 40+ years</span></p></div>
        </div>`;
        return;
      }
      if (this.step === 'owner') {
        title = 'Do you already have a gun?';
        body = `<div class="finder__options">${this.option('yes', 'Yes', this.art('yes'))}${this.option('no', 'No', `<span class="finder__no">${this.art('noCircle')}${this.art('noCross')}${this.art('noCross')}</span>`)}</div>`;
      } else if (this.step === 'brand') {
        title = 'What brand is your price gun?';
        body = `<div class="finder__options">${this.option('Perco', 'Perco', this.modelArt(exampleModel))}${this.option('Monarch', 'Monarch', this.modelArt(this.config.models.find(item => item.key === 'monarch-1131')))}</div>`;
      } else if (this.step === 'model') {
        title = 'Which picture looks most like your gun?';
        body = `<div class="finder__options finder__options--models">${this.config.models.filter(item => item.brand === a.brand).map((item, index) => this.option(item.key, item.name,
          `<span class="finder__model-image finder__model-image--${escape(item.artKey)}">${this.modelArt(item, item.name, true)}</span>`, index === 0)).join('')}</div>
          <button class="finder__link finder__unknown" type="button" data-action="contact">I don't see my gun &rarr;</button>`;
      } else if (this.step === 'print') {
        title = 'What do you need to print?';
        body = `<div class="finder__options">${this.option('price', 'Price', this.labelExample('price', 1))}${this.option('date', 'Date', this.labelExample('date', 1))}</div>`;
      } else if (this.step === 'lines') {
        title = 'How many lines do you need?';
        body = `<div class="finder__options">${this.option('1', '1 Line', this.labelExample(a.print, 1))}${this.option('2', '2 Lines', this.labelExample(a.print, 2))}</div>`;
      } else if (this.step === 'extras') {
        title = 'Do you need labels or ink?';
        body = `<div class="finder__options">${this.option('yes', 'Yes', this.suppliesPhotos(model))}${this.option('no', 'No, just the gun', this.modelArt(model))}</div>`;
      } else if (this.step === 'supplies') {
        title = 'What do you need?';
        body = model?.key === 'monarch-1153'
          ? `<div class="finder__options finder__options--single">${this.option('labels', 'Labels only', this.productPhoto(model?.labels))}</div><p class="finder__center-note">Separate ink is not offered for this model in the quiz.</p>`
          : `<div class="finder__options">${this.option('labels', 'Labels only', this.productPhoto(model?.labels))}${this.option('ink', 'Ink only', this.productPhoto(model?.ink))}${this.option('both', 'Labels + ink', this.suppliesPhotos(model))}</div>`;
      } else if (this.step === 'style') {
        title = 'Which label colors would you like?';
        const styles = core.styleOptions(model?.labels);
        body = `<p class="finder__color-help">Choose one or more colors for your ${escape(model?.name)}. You can confirm how much you use of each color on your offer.</p>
          <div class="finder__options finder__options--styles" role="group" aria-label="Compatible label colors, select one or more">${styles.map(style => this.option(style, style, this.colorPhoto(model, style))).join('')}</div>
          ${!styles.length ? '<p role="status">Compatible label colors are currently unavailable. Please contact us for help.</p>' : ''}`;
      } else if (this.step === 'volume') {
        title = 'How many products do you label per month on average?';
        body = `<div class="finder__usage"><p>Drag the slider or enter your average monthly product count.</p>
          <label for="finder-volume-${escape(this.dataset.sectionId)}">Products per month</label>
          <div class="finder__number"><input id="finder-volume-${escape(this.dataset.sectionId)}" type="number" min="1" max="100000" step="1" value="${a.productsPerMonth}" inputmode="numeric" data-volume><span>products / month</span></div>
          <input type="range" min="1" max="100000" step="1" value="${a.productsPerMonth}" data-volume-slider aria-label="Average products labeled per month">
          <div class="finder__range-labels"><span>1</span><span>100,000</span></div>${this.primary('Continue')}</div>`;
      } else if (this.step === 'contact') {
        title = "Let's find your labeler together.";
        const emailBody = `${a.brand ? `Brand: ${a.brand}\n\n` : ''}Please help identify my price gun and compatible supplies.\n\nAttach a clear photo of the model-number/knob side of your gun before sending.`;
        const emailUrl = `mailto:info@storesupplies.com?subject=${encodeURIComponent('Help identify my price gun')}&body=${encodeURIComponent(emailBody)}`;
        body = `<div class="finder__contact"><svg class="finder__camera" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" aria-hidden="true"><path d="M3 6h4l2-3h6l2 3h4v15H3z"/><circle cx="12" cy="13" r="4"/></svg><p>${a.brand ? `We can help identify your ${escape(a.brand)} gun. ` : ''}Email a clear photo of the model-number/knob side so we can match your supplies.</p><a class="finder__primary" href="${escape(emailUrl)}">Email us a photo &rarr;</a><p>Attach your photo in your email app before sending.</p><p>If your email app does not open, email <a href="${escape(emailUrl)}">info@storesupplies.com</a>.</p><a class="finder__link" href="${escape(this.config.contactUrl)}">Contact us without a photo</a></div>`;
      } else if (this.step === 'results') {
        this.renderResults(focus);
        return;
      }
      this.screen.innerHTML = `${this.navigation()}<div class="finder__question"><h1 tabindex="-1">${escape(title)}</h1>${body}</div>`;
      if (this.querySelector('[data-answer]')) {
        this.querySelector('.finder__question').insertAdjacentHTML('beforeend', `<div class="finder__continue">${this.primary('Continue')}</div>`);
        this.querySelector('.finder__continue button').disabled = !this.hasAnswer();
      }
      if (focus) this.focusHeading();
      this.track('quiz_step_view', { step: this.step });
    }

    navigation() {
      const steps = core.stepsFor(this.answers);
      const current = Math.max(0, steps.indexOf(this.step));
      const total = steps.length;
      const progress = this.step === 'results' ? 10 : Math.max(1, Math.round((current + 1) / Math.max(total, 10) * 10));
      return `<nav class="finder__navigation" aria-label="Quiz navigation"><button type="button" class="finder__link" data-action="back">&larr; Back</button>
        <div class="finder__progress" role="progressbar" aria-label="Quiz progress" aria-valuemin="0" aria-valuemax="10" aria-valuenow="${progress}" aria-valuetext="${this.step === 'results' ? 'Complete' : `${progress * 10}% complete`}"><span style="width: ${progress * 10}%"></span></div>
        <button type="button" class="finder__link" data-action="restart">Start over</button></nav>`;
    }

    mixEditor() {
      const styles = core.selectedStyles(this.answers.style);
      if (this.answers.supplies === 'ink' || styles.length < 2) return '';
      const mix = core.colorMix(styles, this.answers.colorMix);
      return `<fieldset class="finder__mix"><legend>What percentage of your labels are each color?</legend>
        <p>${this.answers.colorMixConfirmed ? 'Confirmed color usage. Change a percentage to update your mix.' : 'Set your actual usage, then confirm. The starting values are editable, not a recommended split.'}</p>
        <div class="finder__mix-inputs">${styles.map(style => `<label>${escape(style)}<span><input type="number" min="0" max="100" step="1" value="${mix[style]}" data-color-percent="${escape(style)}" aria-label="${escape(style)} percentage">%</span></label>`).join('')}</div>
        <p class="finder__mix-total">Total: 100%</p>${this.answers.colorMixConfirmed ? '<p role="status">Color mix confirmed</p>' : '<button type="button" class="finder__primary" data-action="confirm-mix">Confirm color mix</button>'}</fieldset>`;
    }

    resultShell(model, content) {
      return `${this.navigation()}<div class="finder__results finder__results--supplies"><div class="finder__result-visual">${this.modelArt(model)}<h2>${escape(model?.name || 'Your supplies')}</h2></div><div class="finder__result-summary">${content}</div></div>`;
    }

    quizOffers() {
      if (!(this.answers.owner === 'yes' || this.answers.extras === 'yes')) return [];
      const catalog = this.subscriptionCatalog;
      return [6, 3].map(months => {
        const cadence = catalog?.cadences.find(cadence => cadence.unit === 'MONTH' && Number(cadence.count) === months);
        if (!cadence) return { months, plan: null };
        const name = cadence.label || `Every ${months} months`;
        const models = this.config.models.map(model => {
          const next = { ...model };
          for (const kind of ['labels', 'ink']) if (model[kind]) next[kind] = { ...model[kind], variants: model[kind].variants.map(variant => {
            const options = (catalog.optionsByVariantId[String(variant.id)] || []).filter(option => option.planId === catalog.planId && option.cadenceId === cadence.id);
            return { ...variant, plans: variant.plans.filter(plan => options.some(option => String(option.sellingPlanId) === String(plan.id))).map(plan => ({ ...plan, name })) };
          }) };
          return next;
        });
        const result = recommend(models, { ...this.answers, months }, name);
        if (result.error) return { months, plan: null, reason: result.error };
        const variants = {};
        const items = result.lines.map(line => {
          variants[String(line.variant.id)] = { ...line.variant, selling_plan_allocations: line.variant.plans.map(plan => ({ selling_plan_id: plan.id, price: plan.price })) };
          return { id: line.variant.id, quantity: line.quantity, recurring: line.kind !== 'gun' };
        });
        try {
          const priced = globalThis.RevliftSubscriptions.quoteItems(catalog, cadence.id, items, variants, 'product_finder_quiz');
          const quote = { ...result, total: priced.total, lines: result.lines.map((line, index) => ({ ...line, unitPrice: priced.lines[index].unitPrice })) };
          return { ...priced, months, plan: { id: cadence.id, name }, result: quote };
        } catch (error) { return { months, plan: null, reason: error.message }; }
      });
    }

    offerWidgets(offers, oneTime) {
      if (!offers.length) return '';
      const radioName = `finder-offer-${escape(this.dataset.sectionId)}`;
      const oneTimeOriginal = oneTime.lines?.reduce((sum, line) => sum + line.variant.price * line.quantity, 0) || 0;
      const cards = offers.map(offer => {
        const selected = this.offerChoice === String(offer.months);
        const regular = offer.regularRecurringTotal;
        const ink = offer.result?.lines.filter(line => line.kind === 'ink').reduce((sum, line) => sum + line.quantity, 0) || 0;
        return `<label class="finder__offer-card${selected ? ' is-selected' : ''}${!offer.plan ? ' is-unavailable' : ''}">
          <span class="finder__offer-heading"><input type="radio" name="${radioName}" value="${offer.months}" data-offer ${selected ? 'checked' : ''} ${!offer.plan ? 'disabled' : ''}>
            <strong>${offer.months}-month ${offer.result?.incompleteLabels ? 'delivery' : this.answers.supplies === 'ink' ? 'ink delivery' : 'supply'}</strong>${offer.savingsPercent ? `<span class="finder__offer-saving">Save ${offer.savingsPercent}%</span>` : ''}</span>
          ${offer.plan ? `<span class="finder__offer-price">${escape(this.money(offer.recurringTotal))}${regular > offer.recurringTotal ? `<del>${escape(this.money(regular))}</del>` : ''}<span>/ delivery</span></span>
            <span class="finder__offer-billing">Billed ${escape(this.money(offer.recurringTotal))} every ${offer.months} months</span>
            <span class="finder__offer-equivalent">Equivalent to ${escape(this.money(offer.recurringTotal / offer.months))} / month</span>
            ${selected ? `<span class="finder__offer-includes">${offer.result.suppliedLabels ? `${offer.result.suppliedLabels.toLocaleString()} labels in your selected colors` : ''}${offer.result.suppliedLabels && ink ? ' &middot; ' : ''}${ink ? `${ink} ink pack${ink === 1 ? '' : 's'} per delivery` : ''}</span>` : ''}`
            : `<span class="finder__offer-billing">${escape(offer.reason || 'Subscription unavailable for these supplies')}</span>`}
        </label>`;
      }).join('');
      return `<fieldset class="finder__offers"><legend>Subscribe &amp; save</legend>${cards}
        ${oneTime.error ? `<p class="finder__offer-terms">${escape(oneTime.error)}</p>` : `<button class="finder__one-time" type="button" data-action="one-time" aria-label="One-time purchase — ${escape(this.money(oneTime.total))} — Add to Cart" aria-pressed="${this.offerChoice === 'once'}" ${oneTime.lines.length ? '' : 'disabled'}>One-time purchase &mdash; ${oneTimeOriginal > oneTime.total ? `<del><span class="visually-hidden">Original price </span>${escape(this.money(oneTimeOriginal))}</del> ` : ''}<span>${escape(this.money(oneTime.total))}</span></button>`}
        <p class="finder__offer-terms">One-time purchase uses the currently selected package quantities. ${this.answers.owner === 'no' ? 'Your gun is charged once and is not included in recurring deliveries. ' : ''}Ink quantities are complete packs per delivery and can be adjusted below.</p>
      </fieldset>`;
    }

    renderResults(focus) {
      this.pendingResultFocus = this.pendingResultFocus || focus;
      const a = this.answers;
      const model = this.model();
      const wantsInkOffer = (a.owner === 'yes' || a.extras === 'yes') && a.supplies !== 'labels' && model?.ink;
      if (!this.stockReady) {
        this.screen.innerHTML = this.resultShell(model, `<p role="status">${escape(this.stockError || 'Checking current stock and cart quantities...')}</p>`);
        return;
      }
      if ((a.owner === 'yes' || a.extras === 'yes') && !this.hasAttribute('data-revlift-page-integration')) {
        this.screen.innerHTML = this.resultShell(model, '<p role="status">Loading your subscription offer. If it does not load, please reload this page.</p>');
        return;
      }
      if ((a.owner === 'yes' || a.extras === 'yes') && !this.subscriptionCatalog) {
        this.prefetchOffer();
        this.screen.innerHTML = this.resultShell(model, `<p role="status">${escape(this.offerError || 'Loading your saved subscription offer...')}</p>`);
        return;
      }
      if (a.supplies !== 'ink' && core.selectedStyles(a.style).length > 1 && !a.colorMixConfirmed) {
        this.result = null;
        this.mode = '';
        this.screen.innerHTML = this.resultShell(model, this.mixEditor());
        if (this.pendingResultFocus) { this.focusHeading(); this.pendingResultFocus = false; }
        return;
      }
      const offers = this.quizOffers();
      if (this.offerChoice === null) this.offerChoice = String(offers.find(offer => offer.plan)?.months || 'once');
      let selectedOffer = offers.find(offer => String(offer.months) === this.offerChoice && offer.plan);
      if (!selectedOffer && this.offerChoice !== 'once') {
        const period = Number(this.offerChoice);
        const selection = recommend(this.config.models, { ...a, months: period });
        const noSupplies = !selection.error && !selection.lines.some(line => line.kind !== 'gun');
        if (noSupplies) a.months = period;
        else selectedOffer = offers.find(offer => offer.plan);
        this.offerChoice = selectedOffer ? String(selectedOffer.months) : 'once';
      }
      if (selectedOffer) a.months = selectedOffer.months;
      if (selectedOffer && !this.lastSupplyOffer) this.lastSupplyOffer = String(selectedOffer.months);
      this.mode = selectedOffer?.plan.name || '';
      const oneTime = this.priceOneTime(recommend(this.config.models, a));
      const result = selectedOffer ? selectedOffer.result : oneTime;
      this.result = result;
      if (result.error) {
        const colors = a.supplies === 'ink' ? [] : core.selectedStyles(a.style);
        this.screen.innerHTML = this.resultShell(model, `${this.mixEditor()}<h1 tabindex="-1">Let's adjust your supply.</h1><p role="alert">${escape(result.error)}</p>
          ${colors.map(style => `<label class="finder__optional"><input type="checkbox" data-include-color="${escape(style)}" ${a.excludedColors?.includes(style) ? '' : 'checked'}> Include ${escape(style)} labels</label>`).join('')}
          ${wantsInkOffer ? `<label class="finder__optional"><input type="checkbox" data-include-ink ${a.includeInk !== false ? 'checked' : ''}> Include ink</label>` : ''}<p>Use Back to change your colors or monthly usage.</p><a class="finder__link" href="${escape(this.config.contactUrl)}">Contact Us</a>`);
        if (this.pendingResultFocus) { this.focusHeading(); this.pendingResultFocus = false; }
        return;
      }
      const mainProduct = a.owner === 'no' ? result.gun : result.lines[0]?.product;
      const mainVariant = result.lines.find(line => line.kind === 'gun')?.variant || result.lines[0]?.variant;
      const originalTotal = result.lines.reduce((sum, line) => sum + line.variant.price * line.quantity, 0);
      const headline = a.owner === 'no' ? `${model.name} - ${a.print === 'date' ? 'Date' : 'Price'} version` : `${model.name} supplies`;
      const warranty = a.owner === 'no' ? result.gun?.warranty : mainProduct?.warranty;
      const warrantyCopy = /^[1-5] Years?$/.test(warranty || '') ? `${warranty} Warranty` : 'Satisfaction Guaranteed';
      const image = (a.owner === 'yes' ? result.lines.find(line => line.kind === 'labels')?.variant.image : null) || mainProduct?.image;
      const firstColorLine = new Set();
      const lineHtml = line => {
        const first = line.style && !firstColorLine.has(line.style);
        if (line.style) firstColorLine.add(line.style);
        return `<li class="finder__result-item">${line.variant.image || line.product.image ? `<img src="${escape(line.variant.image || line.product.image)}" alt="${escape(line.style ? `${line.style} labels` : line.product.title)}" width="64" height="64" loading="lazy">` : ''}<div>
          <span class="finder__eyebrow">${line.kind === 'gun' ? 'Recommended gun' : line.kind === 'ink' ? 'Compatible ink' : 'Compatible labels'}</span>
          ${first ? `<label class="finder__optional"><input type="checkbox" data-include-color="${escape(line.style)}" checked> Include ${escape(line.style)} labels</label>` : ''}
          ${line.kind === 'ink' ? `<label class="finder__optional"><input type="checkbox" data-include-ink="${line.variant.id}" checked> Add ${line.quantity * core.inkUnits(line.variant)} ${result.includedInkers ? 'extra ' : ''}inkers &mdash; ${escape(this.money(line.unitPrice * line.quantity))}</label>` : ''}
          <strong>${escape(line.product.title)}</strong><span>${line.quantity} &times; ${escape(line.variant.title === 'Default Title' ? line.kind === 'ink' ? `Pack of ${core.inkUnits(line.variant)} inkers` : line.kind === 'gun' ? 'Starter kit' : 'Compatible label pack' : line.variant.title)}${line.allocation ? ` &middot; ${escape(line.allocation.name)}` : ' &middot; One-time purchase'}</span></div>
          ${line.kind === 'ink' ? `<label class="finder__ink-quantity">Packs per delivery<input type="number" min="0" max="${Math.min(9999, core.inventoryLimit(line.variant))}" step="1" value="${line.quantity}" data-ink-quantity="${line.variant.id}" aria-label="${escape(line.variant.title)} packs per delivery"></label>` : ''}
          <span class="finder__item-prices">${line.variant.price > line.unitPrice ? `<del><span class="visually-hidden">Original price </span>${escape(this.money(line.variant.price * line.quantity))}</del>` : ''}<b><span class="visually-hidden">Current price </span>${escape(this.money(line.unitPrice * line.quantity))}</b></span></li>`;
      };
      const removedColors = result.colorPlans.filter(plan => plan.excluded).map(plan => `<li class="finder__result-item"><label class="finder__optional"><input type="checkbox" data-include-color="${escape(plan.style)}"> Include ${escape(plan.style)} labels</label><span>Removed from this order</span></li>`).join('');
      const zeroInk = result.inkChoices.filter(line => line.quantity === 0).map(line => `<li class="finder__result-item"><label class="finder__optional"><input type="checkbox" data-include-ink="${line.variant.id}"> Include ${core.inkUnits(line.variant)}-inker pack</label><span>Removed from this order</span><label class="finder__ink-quantity">Packs per delivery<input type="number" min="0" max="${Math.min(9999, core.inventoryLimit(line.variant))}" step="1" value="0" data-ink-quantity="${line.variant.id}" aria-label="${core.inkUnits(line.variant)} inker packs per delivery"></label></li>`).join('');
      const removedInk = wantsInkOffer && !result.inkChoices.length && !result.lines.some(line => line.kind === 'ink')
        ? '<li class="finder__result-item"><label class="finder__optional"><input type="checkbox" data-include-ink> Include extra ink</label><span>Removed from this order</span></li>' : '';
      this.screen.innerHTML = `${this.navigation()}<div class="finder__results${offers.length ? ' finder__results--supplies' : ''}">
        <div class="finder__result-visual"><p class="finder__result-banner">Your results are in</p>
          ${image ? `<img class="finder__result-image" src="${escape(image)}" alt="${escape(headline)}" width="600" height="600">` : this.modelArt(model)}
          <p class="finder__eyebrow">${a.owner === 'yes' ? 'Compatible supplies' : 'Recommended gun'}</p><h2>${escape(headline)}</h2>
          ${a.owner === 'no' && mainVariant ? `<p>${escape(this.money(mainVariant.price))} &middot; one-time purchase</p>` : ''}
        </div>
        <div class="finder__result-summary"><h1 class="visually-hidden" tabindex="-1">${escape(headline)}</h1>
          ${this.mixEditor()}
          ${result.selectedStyles.length ? `<p class="finder__selected-colors"><strong>Selected label colors:</strong> ${result.selectedStyles.map(escape).join(', ')}</p>` : ''}
          ${this.offerWidgets(offers, oneTime)}
          ${result.includedInkers ? `<div class="finder__ink-notice"><strong>Ink already included &mdash; your ${result.suppliedSleeves} label sleeve${result.suppliedSleeves === 1 ? '' : 's'} come with ${result.includedInkers} inker${result.includedInkers === 1 ? '' : 's'}.</strong>${a.supplies !== 'labels' && model.ink ? '<p>Recommended: Keep extra inkers on hand. Don\'t let a $20 ink shortage cost you hundreds in lost sales. Extra ink is optional; uncheck it below to remove it.</p>' : ''}</div>` : ''}
          ${offers.length ? `<details class="finder__offer-breakdown" ${result.includedInkers || result.selectedStyles.length > 1 || a.supplies === 'ink' ? 'open' : ''}><summary>View your items and supply details</summary>` : ''}
          <ul class="finder__result-items">${result.lines.map(lineHtml).join('')}${removedColors}${zeroInk}${removedInk}</ul>
          ${result.suppliedLabels ? `<div class="finder__calculation"><strong>Your supply calculation</strong><p>${Number(a.productsPerMonth).toLocaleString()} products/month &times; 1.10 labels/product &times; ${a.months} months = ${result.labelsNeeded.toLocaleString()} labels needed.</p><p>${result.suppliedSleeves} sleeve${result.suppliedSleeves === 1 ? '' : 's'} supplied &middot; ${result.suppliedLabels.toLocaleString()} labels.</p>${result.colorPlans.length > 1 ? '<p>Supply follows your confirmed color mix. Each included color is rounded up to whole packs.</p>' : ''}<p>Rounded up to available packs.${a.owner === 'no' ? ' Starter-kit labels are additional.' : ''}</p><p>Assumption: 1 label per product + 10% waste = 1.10 labels per product.</p></div>` : ''}
          ${result.colorPlans.length > 1 ? `<p class="finder__color-summary">${result.colorPlans.map(plan => `${escape(plan.style)}: ${plan.percent}% &middot; ${plan.supplied * model.labelsPerSleeve} labels${plan.excluded ? ' (removed)' : ''}`).join('<br>')}</p>` : ''}
          ${wantsInkOffer ? `<p class="finder__ink-assumption">Ink estimate: ${core.labelsForPeriod(a, a.months).toLocaleString()} labels &divide; ${model.labelsPerSleeve.toLocaleString()} labels per sleeve, rounded up = ${result.inkersNeeded} inkers. Assumption: 1 inker per sleeve. Included inkers count toward this estimate; extra ink is supplied in complete packs. Actual ink usage varies.</p>` : ''}
          ${offers.length ? '</details>' : ''}
          ${result.incompleteLabels || result.incompleteInk ? '<p class="finder__coverage-warning" role="status">Your selected items do not cover all of your entered usage for this delivery period. Removed colors and reduced ink quantities are not replaced. Review your selection before adding to cart.</p>' : ''}
          ${!result.lines.length ? '<p role="status">Select at least one item to add to cart.</p>' : ''}
          <div class="finder__total"><span>Total today</span><span class="finder__total-prices">${originalTotal > result.total ? `<del><span class="visually-hidden">Original total </span>${escape(this.money(originalTotal))}</del>` : ''}<strong><span class="visually-hidden">Current total </span>${escape(this.money(result.total))}</strong></span></div>
          ${this.config.discountCode ? '<p>Any eligible quiz discount will be calculated at checkout.</p>' : ''}
          <p class="finder__note">${this.art('truck')} Arrives in 5-7 days</p>
          ${this.primary('Add to Cart', 'add-to-cart')}
          <p class="finder__note">${this.art('shield')} ${escape(warrantyCopy)}</p>
          <p role="status" hidden data-cart-status></p>
          <p class="finder__error" role="alert" hidden data-cart-error></p>
        </div></div>`;
      if (this.pendingResultFocus) { this.focusHeading(); this.pendingResultFocus = false; }
      if (!result.lines.length) this.querySelector('[data-action="add-to-cart"]').disabled = true;
      this.track('quiz_results_view', { model: model.key, labels: result.suppliedLabels });
    }

    async addToCart() {
      return this.addSelectionToCart();
    }

    async addSelectionToCart() {
      if (this.busy || this.cartReviewRequired || this.result?.error || !this.result?.lines?.length) return;
      this.busy = true;
      let button = this.querySelector('[data-action="add-to-cart"]');
      let controls = [...this.querySelectorAll('button,input')].map(control => [control, control.disabled]);
      controls.forEach(([control]) => { control.disabled = true; });
      this.setAttribute('aria-busy', 'true');
      button.textContent = 'Adding to Cart...';
      let error = this.querySelector('[data-cart-error]');
      let status = this.querySelector('[data-cart-status]');
      error.hidden = true;
      status.hidden = true;
      let cartBefore;
      let writeAttempted = false;
      const signature = () => JSON.stringify([this.offerChoice, this.result?.total, this.result?.lines?.map(line => [line.variant.id, line.quantity, line.unitPrice, line.allocation?.id || null])]);
      const expected = signature();
      const requestedLines = this.result.lines;
      const additions = cart => core.addedCartLines(cartBefore, cart, requestedLines);
      try {
        [cartBefore] = await Promise.all([this.refreshAvailability(true), !this.mode ? this.prefetchPagePromo(true) : Promise.resolve()]);
        this.renderResults(false);
        if (this.result?.error || !this.result?.lines?.length || signature() !== expected) {
          if (!this.result?.stockIssue) await this.refreshSavedOffer().catch(() => {});
          throw new Error('No items were added. Availability or pricing changed. Review your updated offer before adding to cart.');
        }
        button = this.querySelector('[data-action="add-to-cart"]');
        error = this.querySelector('[data-cart-error]');
        status = this.querySelector('[data-cart-status]');
        controls = [...this.querySelectorAll('button,input')].map(control => [control, control.disabled]);
        controls.forEach(([control]) => { control.disabled = true; });
        button.textContent = 'Adding to Cart...';
        const root = this.config.rootUrl;
        const drawer = document.querySelector('cart-drawer');
        const sections = drawer?.getSectionsToRender?.().map(section => section.section) || ['cart-icon-bubble'];
        let data;
        const integrationPlan = this.getAttribute('data-revlift-page-integration');
        writeAttempted = true;
        if (this.mode && integrationPlan) {
          const subscriptions = globalThis.RevliftSubscriptions;
          if (!subscriptions) throw new Error('Subscription options are still loading. Please try again.');
          const catalog = this.subscriptionCatalog;
          if (catalog?.planId !== integrationPlan) throw new Error('Your subscription offer changed. Please reload before ordering.');
          const cadence = catalog.cadences.find(option => option.unit === 'MONTH' && Number(option.count) === Number(this.answers.months));
          if (!cadence) throw new Error('This subscription period is unavailable. Please select another option.');
          const added = await subscriptions.addToCart({ planId: integrationPlan, cadenceId: cadence.id,
            source: 'product_finder_quiz', expectedTotal: this.result.total, sections,
            items: this.result.lines.map(line => ({ id: line.variant.id, quantity: line.quantity, recurring: line.kind !== 'gun',
              productHandle: line.product.handle || line.product.url.split('/products/')[1]?.split(/[?#]/)[0],
              properties: { _product_finder_model: this.result.model.key } })) });
          data = added.response;
        } else {
          const response = await fetch(`${root}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ sections, sections_url: window.location.pathname, items: this.result.lines.map(line => ({ id: line.variant.id, quantity: line.quantity,
            ...(line.allocation ? { selling_plan: line.allocation.id } : {}), properties: { _product_finder_model: this.result.model.key } })) }) });
          data = await response.json().catch(() => null);
          if (!response.ok) throw new Error(data?.description || 'Your cart could not be updated. Please try again.');
          this.dispatchEvent(new CustomEvent('on:cart:add', { bubbles: true, detail: { cart: null, items: data?.items, source: 'product-finder' } }));
        }
        if (!data?.items?.length) throw new Error('We could not confirm the cart update. Please check your cart before trying again.');
        const confirmedResponse = await fetch(`${root}cart.js`, { cache: 'no-store' });
        if (!confirmedResponse.ok) throw new Error('We could not confirm the cart update.');
        const confirmed = await confirmedResponse.json();
        if (additions(confirmed).some(({ line, added }) => added < line.quantity)) throw new Error('The full package was not added.');
        this.updateCartStock(confirmed);
        this.track('quiz_add_to_cart', { model: this.result.model.key });
        status.textContent = 'Added to your cart.';
        status.hidden = false;
        const cartUrl = globalThis.theme?.routes?.cart || `${root}cart`;
        if (this.config.discountCode) {
          window.location.assign(`${root}discount/${encodeURIComponent(this.config.discountCode)}?redirect=${encodeURIComponent(cartUrl)}`);
        } else if (drawer?.renderContents && sections.every(section => typeof data.sections?.[section] === 'string') && globalThis.theme?.settings?.afterAtc !== 'page') {
          try {
            await drawer.renderContents(data, false);
            drawer.open(button);
          } catch {
            window.location.assign(cartUrl);
          }
        } else {
          window.location.assign(cartUrl);
        }
      } catch (failure) {
        let message = failure.message;
        if (!writeAttempted && !message.startsWith('No items were added.')) message = `No items were added. ${message}`;
        if (writeAttempted && cartBefore) {
          try {
            const response = await fetch(`${this.config.rootUrl}cart.js`, { cache: 'no-store' });
            if (!response.ok) throw new Error('Cart could not be read');
            const added = additions(await response.json()).filter(item => item.added > 0);
            if (added.length) {
              this.cartReviewRequired = true;
              message = `Your cart was updated with: ${added.map(({ line, added }) => `${added} x ${line.product.title} (${line.variant.title})`).join('; ')}. The full package could not be confirmed. Review your cart before adding anything again.`;
            } else {
              message = 'No items were added. Current stock or pricing prevented this package from being added. Please review your selection and try again.';
              // Refresh the displayed quote after a failed validation, never retry the write.
              await Promise.all([this.refreshAvailability(true), this.refreshSavedOffer()]).then(() => this.renderResults(false)).catch(() => {});
            }
          } catch {
            this.cartReviewRequired = true;
            message = 'We could not confirm whether items were added. Please review your cart before trying again.';
          }
        }
        error = this.querySelector('[data-cart-error]');
        if (!error) {
          error = document.createElement('p');
          error.className = 'finder__error'; error.setAttribute('role', 'alert'); error.dataset.cartError = '';
          this.querySelector('.finder__result-summary').append(error);
        }
        error.textContent = message;
        const review = document.createElement('a');
        review.href = `${this.config.rootUrl}cart`; review.textContent = ' View your cart';
        error.append(review);
        error.hidden = false;
      } finally {
        this.busy = false;
        this.removeAttribute('aria-busy');
        controls.forEach(([control, disabled]) => { if (control.isConnected) control.disabled = disabled; });
        const currentButton = this.querySelector('[data-action="add-to-cart"]');
        if (currentButton) currentButton.innerHTML = `Add to Cart ${this.art('arrow', 'finder__arrow')}`;
        if (this.cartReviewRequired) this.querySelectorAll('[data-action="add-to-cart"], [data-action="one-time"]').forEach(control => { control.disabled = true; });
      }
    }

    focusHeading() {
      const heading = this.screen.querySelector('h1') || this.screen.querySelector('h2');
      if (heading) { heading.setAttribute('tabindex', '-1'); heading.focus({ preventScroll: true }); }
      this.scrollIntoView({ block: 'start', behavior: 'instant' });
    }

    track(name, detail) { this.dispatchEvent(new CustomEvent(name, { bubbles: true, detail })); }
  }
  customElements.define('product-finder', ProductFinder);
})();
